/**
 * Card Payment Input Component
 */
import { getDeviceInfo } from "../../Utils/deviceInfo";
import { getEncrypDecryptStr } from "../../Utils/encryptionDecryption";
import React, { useEffect, useState, FC, HTMLAttributes, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { initPayment, setEmiOptions, getEmiOptionsSpecificToCard } from "../../Redux/Actions";
import {
  cardNumberFormat,
  formatCvc,
  formatExpiry,
  getCardType,
  isCardValid,
  isExpiryInvalid,
} from "../../Utils/cardTypeValidator";
import config, { CURRENCY_SYMBOL } from "../../Config";
import { RootState } from "../../Redux/Reducers";
import LocalStorageHelper from "../../Utils/LocalStorageHelper";
import "./styles.scss";
// import InfoIcon from  "../../Assets/images/info.png";
import InfoIcon from "../../Assets/images/info-gray.png";
import loaderIcon from "./../../Assets/images/ellipLoader1.gif";
import DownCaretIcon from "../../Assets/images/caret-down.svg";
import { ENCRYPTED_CARD_DETAILS } from "../../Constants/localStorageKeys";
import { PAYMENT_TYPE_CONSTANTS } from "../../Constants";
import { clevertapEventPush } from "../../Utils/common";
import { EMI_INFORMATION_STATE } from "./EMIPaymentBox";
import CustomRadio from "../../Components/CustomRadio";
import { CARD_TYPE } from "../../Utils/constants";
import { debounce } from "../../Utils/_helper";
import { ToastNotifyError } from "../Toast";
import { useHistory } from "react-router-dom";
const rangeOfYears = () => {
  const result = [];
  let min = new Date().getUTCFullYear();
  const max = min + 20;
  while (min <= max) {
    result.push("" + min);
    min++;
  }
  return result;
};

const EMIPaymentBox: FC<any> = (props: any) => {
  const dispatch = useDispatch();
  const router = useHistory();
  // State Variables for EMI
  const [emiInformation, setEmiInformation] = useState<EMI_INFORMATION_STATE>({
    cardInformation: {
      cardNumber: "",
      monthSelected: "01",
      yearSelected: rangeOfYears()[0],
      cvv: "",
    },
    cvvLength: 3,
    selectedEmi: {},
    step: 1,
    completedSteps: [],
    cardType: "",
    emiAvailable: true,
  });
  const [openEmiOption, setOpenEmiOption] = useState(0);
  const [cardValidation, setCardValidation] = useState({
    isCardNumberValid: true,
    isCardCVVInvalid: false,
  });
  const [showCvvShowcase, setShowCvvShowcase] = useState(false);

  /** Icon CDN's for cards */
  const cc_icons: any = {
    VISA: config.CDN_SERVER_URL + "image/visa.png",
    MASTERCARD: config.CDN_SERVER_URL + "image/mastercard.png",
    AMEX: config.CDN_SERVER_URL + "image/amex.png",
    MAESTRO: config.CDN_SERVER_URL + "image/maestro.png",
    DebitCard: config.CDN_SERVER_URL + "image/sample_card.png",
    RUPAY: config.CDN_SERVER_URL + "custom_website/RuPay.svg",
  };

  const { emiOptionsForCard, orderDetails, storeData, userData, prePaymentData, loader } = useSelector(
    (state: RootState) => ({
      emiOptionsForCard: state.paymentMethodReducer.emiOptionsForCard,
      orderDetails: state.ordersReducer.orderDetails,
      prePaymentData: state.paymentMethodReducer.prePaymentData,
      storeData: state.storeReducer.store,
      loader: state.commonReducer.pageLoaderVisibility,
      userData: state.userReducer,
    }),
  );

  /**
   * UseEffect for prefilled card data
   */
  // useEffect(() => {
  //   let newState = { ...state };
  //   if (LocalStorageHelper.get(ENCRYPTED_CARD_DETAILS)) {
  //     let cardDetails = getEncrypDecryptStr(LocalStorageHelper.get(ENCRYPTED_CARD_DETAILS), "decrypt");
  //     cardDetails = JSON.parse(cardDetails);
  //     newState = {
  //       ...state,
  //       cvv: "",
  //       expiry: cardDetails?.cExpMonth + "/" + cardDetails?.cExpYear.slice(-2),
  //       number: cardNumberFormat(cardDetails?.cNum),
  //       cardType: getCardType(cardDetails?.cNum),
  //       cvvLength: getCardType(cardDetails?.cNum) === "AMEX" ? 4 : 3,
  //     };
  //   }
  //   setState(newState);
  // }, []);

  const toggleCvvPopup = () => {
    setShowCvvShowcase((s) => !s);
  };
  const handleCardNumberChange = (value) => {
    if (value.length >= 6) {
      const callback = (emiOptionAvailable) => {
        setEmiInformation((state) => ({
          ...state,
          emiAvailable: emiOptionAvailable,
        }));
      };
      /**
       * change card_number in obj to allow only the first 6 digits
       * in card number to be sent as payload for initiatePayment api
       * 
       * later to use 
       * {
            store_id: storeData?.store_id,
            hash: prePaymentData?.orderID,
            card_number: value.substring(0, 6),
          }
       * 
       */
      dispatch(
        getEmiOptionsSpecificToCard(
          {
            hash: prePaymentData?.orderID,
            card_number: value.substring(0, 6),
          },
          callback,
        ),
      );
    }
  };
  const debounceOnChange = useCallback(debounce(handleCardNumberChange, 500), []);
  const validateAll = () => {
    const newState = { ...cardValidation };
    let isAllValid = true;
    if (!emiInformation.cardInformation.cardNumber) {
      newState["isCardNumberValid"] = false;
      isAllValid = false;
    }
    if (emiInformation.cvvLength !== emiInformation.cardInformation.cvv.length) {
      newState["isCardCVVInvalid"] = true;
      isAllValid = false;
    }
    setCardValidation(newState);
    return isAllValid;
  };

  const validateCardNumber = (number) => {
    const newState = { ...cardValidation };
    newState["isCardNumberValid"] = isCardValid(number);
    setCardValidation(newState);
  };

  const validateCardCVV = (number) => {
    const newState = { ...cardValidation };
    newState["isCardCVVInvalid"] = number.length !== emiInformation.cvvLength;
    setCardValidation(newState);
  };

  const inputChange = (value, type) => {
    const tempState = {
      ...emiInformation,
      cardInformation: { ...emiInformation.cardInformation },
    };
    const tempCardValidation = { ...cardValidation };
    switch (type) {
      case "cardNo": {
        tempCardValidation.isCardNumberValid = true;
        if (value.length <= 20) {
          tempState.cardInformation.cardNumber = cardNumberFormat(value.trim());
          tempState.cardType = getCardType(value.trim());
          tempState.emiAvailable = true;
          tempState.cvvLength = getCardType(tempState.cardInformation.cardNumber) === CARD_TYPE.AMEX ? 4 : 3;
          debounceOnChange(value.trim());
        }
        break;
      }
      case "cardMonth": {
        tempState.cardInformation.monthSelected = value;
        break;
      }
      case "cardYear": {
        tempState.cardInformation.yearSelected = value;
        break;
      }
      case "cvv": {
        tempCardValidation.isCardCVVInvalid = false;
        if (value.length <= emiInformation.cvvLength) {
          tempState.cardInformation.cvv = value;
        }
        break;
      }
      default:
        break;
    }
    setEmiInformation((state) => ({
      ...state,
      ...tempState,
    }));
    setCardValidation((state) => ({
      ...state,
      ...tempCardValidation,
    }));
  };
  const CompletedStepComponent = ({ children }) => <div className="emi-step-completed">{children}</div>;
  const selectEmiPlanBtnClick = (emiPlan) => {
    setEmiInformation((state) => ({
      ...state,
      selectedEmi: emiPlan,
    }));
    completeEmiSteps(2);
    const payload = {
      order_hash: prePaymentData?.orderID,
      store_id: storeData?.store_id,
      emi_option: { ...emiPlan },
    };

    const callback = (status) => {
      if (status) {
        completeEmiSteps(2);
      } else {
        ToastNotifyError("Cannot select emi plan currently");
      }
    };
    // dispatch(setEmiOptions(payload, callback));
  };
  const disabilityOfMobileButton = () => {
    switch (emiInformation.step) {
      case 1:
        return !(emiInformation.cardInformation.cardNumber.length >= 16) || !emiInformation.emiAvailable;
      case 3:
        return emiInformation.cardInformation.cvv.length !== emiInformation.cvvLength;
      default:
        return true;
    }
  };
  const completeEmiSteps = (step) => {
    if (
      !emiInformation.emiAvailable ||
      emiInformation.cardInformation.cardNumber === "" ||
      emiInformation.cardInformation.cardNumber.split(" ").join("").length < 14
    ) {
      return;
    }
    const tempCompletedSteps = [...emiInformation.completedSteps];
    tempCompletedSteps.push(step);
    const newStepCount = emiInformation.step === 3 ? 3 : emiInformation.step + 1;

    setEmiInformation((state) => ({
      ...state,
      completedSteps: [...new Set(tempCompletedSteps)],
      step: newStepCount,
    }));
  };
  const completeEmiPaymentInit = () => {
    if (validateAll()) {
      const data = {
        orderID: prePaymentData?.orderID,
        paymentMethod: PAYMENT_TYPE_CONSTANTS.EMI,
        merchantID: 2 /** doubt about merchantId */,
        deviceType: getDeviceInfo().OSName,
        browserType: getDeviceInfo().BrowserName,
        userAgent: getDeviceInfo().userAgent,
        source: "premium",
        cNum: emiInformation.cardInformation.cardNumber.split(" ").join(""),
        cCvv: emiInformation.cardInformation.cvv,
        cName: "DOTPE",
        cExpMonth: emiInformation.cardInformation.monthSelected,
        cExpYear: emiInformation.cardInformation.yearSelected,
        merchantName: `Digital Showroom - ${storeData?.store_info?.name}`,
        emiCode: emiInformation.selectedEmi.emi_code,
        phone: userData?.data?.phone,
      };
      const encryptCCDetails = getEncrypDecryptStr(JSON.stringify(data), "encrypt");
      localStorage.setItem("dscc", JSON.stringify(encryptCCDetails));
      dispatch(initPayment(data, router));
    }
  };

  const emiStepCompletionForMobile = () => {
    switch (emiInformation.step) {
      case 1:
        completeEmiSteps(1);
        break;
      case 3:
        completeEmiPaymentInit();
        break;
      default:
        break;
    }
  };
  const affirmationButton = ({ onClick = null, disabled = false, label = "Continue", showButton = true }) => {
    if (!showButton) {
      return null;
    }
    return (
      <button
        disabled={disabled}
        className={`pointer dib affirmation-btn fw6-ns fw5 br-pill ${disabled ? "o-30" : ""}`}
        onClick={onClick}
      >
        {label}
      </button>
    );
  };
  const cardNumberRenderAfterStep1 = () => {
    // here slice starts at 7th inex due to 1 space between card number blocks
    const firstSixChar = emiInformation.cardInformation.cardNumber.substring(0, 7);
    const mappedLastChars = emiInformation.cardInformation.cardNumber.slice(7).split(" ");
    const morphString = mappedLastChars.map((value) => value.replace(/./gi, "X"));
    return `${firstSixChar}${morphString.join(" ")}`;
  };
  const editEmiSteps = (step) => {
    setEmiInformation((state) => ({
      ...state,
      completedSteps: step === 1 ? [] : [1],
      step: step,
    }));
  };
  const cardBaseErrorMessage = () => {
    if (!emiInformation.emiAvailable) {
      return <small className="error">No EMI option available for this Bank, please try different card</small>;
    }
    if (!cardValidation.isCardNumberValid) {
      return <small className="error">Enter valid card number</small>;
    }
    return null;
  };
  const EditButton = ({ onClick = undefined, label = "", customStyle = {} }) => 
    <span className={`relative pointer dib edit-btn br-pill`} onClick={onClick} style={customStyle}>
      {label ?? "Edit"}
    </span>
  ;
  const cardInformationSection = () => {
    const handleCardNumberChnage = (e) => {
      inputChange(e.target.value.replace(/[^0-9]/g, ""), "cardNo");
      e.target.value.length >= 10 && validateCardNumber(e.target.value.replace(/[^0-9]/g, ""));
    };
    return (
      <div className="emi-wrapper-box">
        <h3 className="emiCardPayment__header">Enter Credit or Debit card details</h3>
        <div className={`card-information-section flex ${props.isDesktop ? "flex-row" : "flex-column"}`}>
          <div className="card-information-section--first">
            <div className="flex items-center justify-between">
              <div className="card-header-text">Card Number</div>
              {!props.isDesktop && emiInformation.completedSteps.includes(1) && 
                <span className="card-header-edit dib" onClick={() => editEmiSteps(1)}>
                  Edit Card
                </span>
              }
            </div>
            <div className="relative">
              {emiInformation.completedSteps.includes(1) ? 
                <CompletedStepComponent>
                  <p className="mv0">{cardNumberRenderAfterStep1()}</p>
                </CompletedStepComponent>
               : 
                <>
                  <input
                    type="tel"
                    className={`emi-input w-100 ${
                      !emiInformation.emiAvailable || !cardValidation.isCardNumberValid ? "invalid" : ""
                    }`}
                    value={emiInformation.cardInformation.cardNumber}
                    placeholder="XXXX XXXX XXXX XXXX"
                    onChange={(e) => handleCardNumberChnage(e)}
                    onBlur={(e) => validateCardNumber(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  {cardBaseErrorMessage()}
                </>
              }
              {emiInformation?.cardType ? 
                <img
                  className="card-image"
                  loading="lazy"
                  src={cc_icons[emiInformation.cardType] || config.CDN_SERVER_URL + "image/ic-credit-card.svg"}
                  alt="cardType"
                />
               : null}
            </div>
          </div>
          <div className="card-information-section--second flex flex-column justify-end">
            {emiInformation.completedSteps.includes(1) ? 
              props.isDesktop && 
                <EditButton label="Edit Card" customStyle={{ bottom: 8 }} onClick={() => editEmiSteps(1)} />
              
             : 
              <>
                <div className="card-header-text">Expiry Date</div>
                <div className="flex items-center">
                  <span className="relative mr3">
                    <select
                      value={emiInformation.cardInformation.monthSelected}
                      className="emi-select"
                      onChange={(e) => inputChange(e.target.value, "cardMonth")}
                    >
                      {Array.from(Array(12).keys()).map((arr) => 
                        <option key={arr}>{`${+arr + 1 < 10 ? "0" : ""}${arr + 1}`}</option>,
                      )}
                    </select>
                    <img alt="dropdown button" src={DownCaretIcon} />
                  </span>
                  <span className="relative">
                    <select
                      value={emiInformation.cardInformation.yearSelected}
                      className="emi-select"
                      onChange={(e) => inputChange(e.target.value, "cardYear")}
                    >
                      {rangeOfYears()?.map((range) => <option key={range}>{range}</option>)}
                    </select>
                    <img alt="dropdown button" src={DownCaretIcon} />
                  </span>
                </div>
              </>
            }
          </div>
        </div>
        {(!emiInformation.completedSteps.includes(1) || emiInformation.step === 1) && 
          <div className="flex justify-end" style={{ marginTop: 32 }}>
            {affirmationButton({
              showButton: props.isDesktop,
              disabled:
                !cardValidation.isCardNumberValid ||
                !(emiInformation.cardInformation.cardNumber.length >= 16) ||
                !emiInformation.emiAvailable,
              onClick: () => completeEmiSteps(1),
            })}
          </div>
        }
      </div>
    );
  };
  const emiSelectionComponent = () => {
    return (
      <div className="emi-selection-section mb4-ns">
        <div className="flex items-center justify-between">
          <h3 className="emiCardPayment__header">Select EMI Tenure</h3>
          {!props.isDesktop && emiInformation.completedSteps.includes(2) && 
            <span className="card-header-edit dib" onClick={() => editEmiSteps(2)}>
              Edit Tenure
            </span>
          }
        </div>
        {emiInformation.completedSteps.includes(2) ? 
          <div className="flex items-center">
            <div className="card-information-section--first">
              <CompletedStepComponent>
                <p className="mv0">
                  <b>
                    {CURRENCY_SYMBOL}
                    {emiInformation?.selectedEmi?.payable_emi_value || ""}
                  </b>{" "}
                  X {emiInformation?.selectedEmi?.tenure} | Total Cost - {CURRENCY_SYMBOL}
                  {emiInformation?.selectedEmi?.total_payable_amount}
                </p>
              </CompletedStepComponent>
            </div>
            {props.isDesktop && 
              <div
                className="card-information-section--second flex flex-column justify-end"
                style={{
                  paddingLeft: 0,
                }}
              >
                <EditButton label="Edit Tenure" onClick={() => editEmiSteps(2)} />
              </div>
            }
          </div>
         : 
          <div
            className="overflow-hidden"
            style={{
              border: "0.4px solid #D6D6D6",
              borderRadius: 7,
            }}
          >
            {emiOptionsForCard?.length > 0 &&
              emiOptionsForCard?.map((emi, index) => 
                <div className="emi-option-accordion" key={index}>
                  <div
                    className="emi-option-accordion__header flex items-center pointer"
                    onClick={() => setOpenEmiOption(index)}
                  >
                    <CustomRadio
                      readOnly
                      className="mr3 ultramarine-blue-color"
                      type="radio"
                      checked={openEmiOption === index}
                      onChange={undefined}
                    />
                    <p className="mv0">
                      <b className="fw6">
                        {CURRENCY_SYMBOL}
                        {emi.payable_emi_value}
                      </b>{" "}
                      X {emi.tenure} | Total Cost - {CURRENCY_SYMBOL}
                      {emi.total_payable_amount}
                    </p>
                  </div>
                  {openEmiOption === index && 
                    <div className="emi-option-accordion__body">
                      <table className="emi-table w-100">
                        <thead>
                          <tr>
                            <th>{props.isDesktop ? "EMI per month" : "EMI Plan"}</th>
                            <th>{props.isDesktop ? "Interest" : "Interest(pa)"}</th>
                            <th>{props.isDesktop ? "Total EMI Cost" : "Total Cost"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <b className="fw6">
                                {CURRENCY_SYMBOL}
                                {emi.payable_emi_value}
                              </b>
                            </td>
                            <td>
                              {CURRENCY_SYMBOL}
                              {emi.total_emi_interest_paid}({emi.emi_bank_interest}%)
                            </td>
                            <td>
                              {CURRENCY_SYMBOL}
                              {emi.total_payable_amount}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="tr-ns tc pt3">
                        {affirmationButton({
                          label: "Select EMI Plan",
                          onClick: () => selectEmiPlanBtnClick(emi),
                        })}
                      </div>
                    </div>
                  }
                </div>,
              )}
          </div>
        }
      </div>
    );
  };
  const cvvShowcasePopup = () => 
    <div className="cvvShowcasePopup z-5">
      <p>The CVV number is the last three digits at the back of your card.</p>
      <img src={`${process.env.REACT_APP_THEME_PREVIEW_URL}assets/images/cvv-showcase-mobile.png`} />
    </div>
  ;
  const checkForValidCVV = () => emiInformation.cardInformation.cvv.length !== 0 && cardValidation.isCardCVVInvalid;

  const cardCvvInput = () => {
    return (
      <div className="mb4 relative">
        <h3 className="emiCardPayment__header relative flex items-center">
          Enter CVV <img src={InfoIcon} onMouseOver={toggleCvvPopup} onMouseOut={toggleCvvPopup} />
        </h3>
        {showCvvShowcase && cvvShowcasePopup()}
        <div className="flex items-center relative">
          <input
            type="tel"
            className={`emi-input mr3 w-20-ns w-40 ${checkForValidCVV() ? "invalid" : ""}`}
            value={emiInformation.cardInformation.cvv}
            onChange={(e) => inputChange(e.target.value.replace(/[^0-9]/g, ""), "cvv")}
            onBlur={(e) => validateCardCVV(e.target.value)}
          />
          {checkForValidCVV() && <small className="error">{cardValidation.isCardCVVInvalid}</small>}
          {affirmationButton({
            showButton: props.isDesktop,
            disabled: emiInformation.cardInformation.cvv.length !== emiInformation.cvvLength,
            onClick: completeEmiPaymentInit,
          })}
        </div>
      </div>
    );
  };
  return (
    <section className="card-payment-box">
      <div className="emiCardPayment pa4">
        {/* {!props.isDesktop && (
      <div className="page-header flex items-center">
        <BackArrow onClick={() => router.back()} />
        <span className="header-text ml3 fw6">EMI</span>
      </div>
    )} */}
        <div className="emiCardPayment__body">
          {/* card information section */}
          {cardInformationSection()}
          {/* EMI selection */}
          {emiInformation.step >= 2 && emiSelectionComponent()}
          {/* Enter cvv */}
          {emiInformation.step === 3 && cardCvvInput()}
        </div>
        {/* button to show only in mobile and for steps 1 and 3 */}
        {!props.isDesktop && emiInformation.step !== 2 && 
          <div className="mobile-affirm-btn-container">
            {affirmationButton({
              showButton: !props.isDesktop,
              disabled: disabilityOfMobileButton(),
              onClick: emiStepCompletionForMobile,
            })}
          </div>
        }
      </div>
    </section>
  );
};

export default EMIPaymentBox;
